import React, { useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger } from 'src/components/ui/select';
import moment from 'moment';
import { clsx } from 'clsx';
import { useAppSelector } from 'src/store';
import { ConfigurationSettingsAddNewPopup } from 'src/pages/ConfigurationPage/ConfigurationSettingsVersions/ConfigurationSettingsAddNewPopup/ConfigurationSettingsAddNewPopup';
import type { ConfigurationSettings } from 'src/services/configurationService';

// What the version select reports: an existing version, or the request to start a new one.
export type SettingsSelection = ConfigurationSettings | 'new-version';

export const ConfigurationSettingsVersions = ({
  readonly,
  activeSettings,
  settings,
  onChange = () => {},
  onCreate = () => {},
}: {
  readonly?: boolean;
  activeSettings: ConfigurationSettings | null;
  settings: ConfigurationSettings[];
  onChange?: (selection: SettingsSelection) => void;
  onCreate?: (isManual: boolean) => void;
}) => {
  const [open, setOpen] = React.useState(false);
  const [popupOpened, setPopupOpened] = React.useState(false);
  // null until a version is picked; the trigger then shows "Version 1".
  const [selected, setSelected] = React.useState<ConfigurationSettings | null>(null);
  const [selectedId, setSelectedId] = React.useState<number | string | null>(null);
  const { isInactivePlan } = useAppSelector((s) => s.planSlice);

  useEffect(() => {
    if (activeSettings) {
      setSelectedId(activeSettings.id);
    }
  }, [activeSettings]);

  useEffect(() => {
    if (selectedId === 'new-version') {
      onChange(selectedId);

      return;
    }

    const setting = settings.find((item) => item.id === +selectedId);

    if (setting) {
      setSelected(setting);
    }
  }, [selectedId]);

  useEffect(() => {
    if (selected) {
      onChange(selected);
    }
  }, [selected]);

  const onOpenChange = (opened: boolean) => {
    popupOpened ? setOpen(true) : setOpen(opened);
  };

  const close = () => {
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-3">
      <form id="configuration-settings">
        <div className={'input-container'}>
          <Select
            disabled={!readonly}
            defaultValue={'none'}
            open={open}
            onOpenChange={onOpenChange}
            onValueChange={(value) => {
              setSelectedId(value);
            }}
          >
            <SelectTrigger
              className={clsx(
                'w-full bg-transparent [&_svg]:transition-transform border-none gap-2 px-3 py-2 !min-h-5 !text-sm',
                readonly && 'hover:bg-white',
                open && '[&_svg]:rotate-180 bg-white',
              )}
            >
              <div>
                <p className={'!text-[13px] font-medium text-tertiary'}>
                  <span>Version {selected ? selected.version : '1'}</span>
                </p>
              </div>
            </SelectTrigger>
            <SelectContent side={'bottom'} align={'end'} className={'bg-light'}>
              {!isInactivePlan && (
                <ConfigurationSettingsAddNewPopup
                  setPopupOpened={setPopupOpened}
                  onCreate={onCreate}
                  closeSettingsSelect={close}
                />
              )}
              {settings.map((setting) => {
                return (
                  <SelectItem
                    className={'px-4'}
                    key={setting.id}
                    value={String(setting.id)}
                    indicatorHidden
                  >
                    <div className="flex flex-col">
                      <div className="flex gap-2 items-center">
                        <p
                          className={clsx(
                            'text-sm text-start relative',
                            setting.id === activeSettings?.id ? 'font-bold' : 'font-medium',
                          )}
                        >
                          <span>
                            Version {setting.version}{' '}
                            {setting.updated_at !== setting.created_at
                              ? `(deployed on ${moment(setting.updated_at).format('MMM DD, YYYY')}) `
                              : ''}
                          </span>
                          {Boolean(setting.is_deployed) && (
                            <span
                              className={
                                'text-[#389D61] text-xs font-medium rounded-sm-5 border border-[#389d6126] bg-[#F4FFF7] px-2 py-1'
                              }
                            >
                              Deployed
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </form>
    </div>
  );
};
